import React from 'react'
import moment from 'moment'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/lib/ui/dialog'
import { Button } from '~/components/_atoms'
import AttachmentDisplay from '../candidate/Chat/AttachmentDisplay'

const ChatDetailsModal = ({ chat, onClose, getDarkModeClasses, t }) => {
  if (!chat || !chat.chat_text) {
    return null
  }

  // Helper function to format message timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return ''
    // Handle both millisecond and second timestamps
    const ts =
      typeof timestamp === 'number' && timestamp < 10000000000
        ? timestamp * 1000
        : timestamp
    return moment(ts).format('LT') // Local time format (e.g., "2:30 PM")
  }

  // Helper function to render message content safely
  const renderMessageContent = (content) => {
    if (!content) return ''

    // Check if this is a "sent {filename}" message and apply ellipsis to filename
    const sentFileMatch = content.match(/^(sent\s+)(.+)$/i)
    if (sentFileMatch) {
      const [, prefix, filename] = sentFileMatch
      // Escape HTML
      const escapedPrefix = prefix
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      const escapedFilename = filename
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')

      return (
        <span>
          {escapedPrefix}
          <span
            className="inline-block max-w-[200px] truncate align-bottom"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={filename}
          >
            {escapedFilename}
          </span>
        </span>
      )
    }

    // Escape HTML and convert line breaks to <br> tags
    const escapedContent = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br />')

    return <span dangerouslySetInnerHTML={{ __html: escapedContent }} />
  }

  // Helper function to determine if message is from the coach/user
  const isOwnMessage = (message) => {
    // Check sender field first (primary method)
    if (message.sender === 'coach') {
      return true
    }
    // Fallback to checking who field for legacy messages
    const who = message.who || ''
    return (
      who.toLowerCase() === 'me' ||
      who.toLowerCase() === 'you' ||
      who === 'system'
    )
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent
        className={`font-roboto lg:max-w-4xl ${getDarkModeClasses(
          'bg-white',
          'bg-gray-800',
        )}`}
        variant="default"
      >
        <DialogHeader>
          <DialogTitle className="text-left" asChild>
            <h2
              className={`text-2xl ${getDarkModeClasses('text-white', 'text-white')}`}
            >
              {t('chat_details.title', {
                default: 'Chat with %{candidate_name}',
                candidate_name: chat.candidate_name,
              })}
            </h2>
          </DialogTitle>
        </DialogHeader>
        <DialogDescription
          className={getDarkModeClasses('text-gray-600', 'text-gray-300')}
        >
          {t('chat_details.description', 'Full chat transcript')}
        </DialogDescription>
        <div
          className={`max-h-[70vh] overflow-y-auto px-6 py-4 ${getDarkModeClasses(
            'bg-white',
            'bg-gray-800',
          )}`}
        >
          {chat.chat_text
            .filter((message) => {
              // Filter out messages with empty text (system messages like candidate_action)
              // These are handled separately or should not be displayed
              return message.text && message.text.trim().length > 0
            })
            .map((message, index) => {
              const ownMessage = isOwnMessage(message)
              const attachmentUrl =
                message.attachment_url ||
                message.action_target ||
                message.attachment?.url
              const attachment = message.attachment?.url
                ? message.attachment
                : attachmentUrl
                  ? {
                      url: attachmentUrl,
                      name: message.attachment_name || message.attachment?.name,
                      size: message.attachment?.size,
                      type: message.attachment?.type,
                    }
                  : null
              const hasAttachment = !!attachment

              return (
                <div
                  key={index}
                  className={`mb-4 flex ${ownMessage ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[320px] px-4 py-3 ${
                      ownMessage
                        ? `rounded-l-xl rounded-t-xl ${getDarkModeClasses('bg-cod-gray-100 text-gray-900', 'bg-gray-700 text-white')}`
                        : 'rounded-r-xl rounded-t-xl bg-intoo-green-medium-800 text-white'
                    }`}
                  >
                    {/* Render attachment if present (from attachment object or attachment_url) */}
                    {hasAttachment && (
                      <div className="mb-2">
                        <AttachmentDisplay
                          attachment={attachment}
                          isOwnMessage={ownMessage}
                          getDarkModeClasses={getDarkModeClasses}
                        />
                      </div>
                    )}

                    {/* Render message content if present - as link when attachment URL exists */}
                    {message.text && (
                      <div
                        className={`text-base font-normal [&_a]:!cursor-pointer [&_a]:!underline ${
                          ownMessage
                            ? getDarkModeClasses(
                                '[&_a]:text-gray-900',
                                '[&_a]:text-white',
                              )
                            : '[&_a]:text-white'
                        }`}
                      >
                        {attachmentUrl ? (
                          <a
                            href={attachmentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:opacity-90"
                          >
                            {renderMessageContent(message.text)}
                          </a>
                        ) : (
                          renderMessageContent(message.text)
                        )}
                      </div>
                    )}

                    {/* Timestamp at bottom */}
                    {message.ts && (
                      <div
                        className={`mt-1 text-xs ${
                          ownMessage
                            ? getDarkModeClasses(
                                'text-gray-600',
                                'text-gray-300',
                              )
                            : 'text-intoo-green-medium-100'
                        }`}
                      >
                        {formatTimestamp(message.ts)}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
        </div>
        <div className="flex flex-row justify-end p-4">
          <Button variant="outline" onClick={onClose}>
            {t('chat_details.close', 'Close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ChatDetailsModal
